import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { uploadMenuImage } from "../utils/api";
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaTimes,
  FaToggleOn,
  FaToggleOff,
  FaSearch,
  FaBars,
} from "react-icons/fa";

export default function Menus() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [editingMenuId, setEditingMenuId] = useState(null);
  const [editingMenuName, setEditingMenuName] = useState("");
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemForm, setItemForm] = useState({});
  const [itemImageFile, setItemImageFile] = useState(null);
  const [itemUploading, setItemUploading] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const toastTimeout = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [search, setSearch] = useState("");
  const [showOutOfStock, setShowOutOfStock] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [menuModalMode, setMenuModalMode] = useState("add");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchMenus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/menus");
      setMenus(res.data);
      // Auto-select first menu if none selected
      if (res.data.length > 0 && !activeMenuId) {
        setActiveMenuId(res.data[0].id);
      }
    } catch (err) {
      showToast("Failed to load menus", "error");
    }
    setLoading(false);
  }, [activeMenuId]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  async function handleCreateMenu(e) {
    e.preventDefault();
    try {
      await api.post("/menus", { name });
      setName("");
      fetchMenus();
      showToast("Menu added!");
    } catch (err) {
      showToast("Failed to create menu", "error");
    }
  }

  async function handleDeleteMenu(id) {
    if (!window.confirm("Delete this menu and all its items?")) return;
    try {
      await api.delete(`/menus/${id}`);
      fetchMenus();
      showToast("Menu deleted!");
    } catch (err) {
      showToast("Failed to delete menu", "error");
    }
  }

  async function handleUpdateMenu(id) {
    try {
      await api.put(`/menus/${id}`, { name: editingMenuName });
      setEditingMenuId(null);
      setEditingMenuName("");
      fetchMenus();
      showToast("Menu updated!");
    } catch (err) {
      showToast("Failed to update menu", "error");
    }
  }

  function openItemModal(menuId, item = null) {
    setActiveMenuId(menuId);
    setItemForm(
      item
        ? { ...item }
        : {
            name: "",
            price: "",
            description: "",
            imageUrl: "",
            active: true,
          }
    );
    setItemImageFile(null);
    setShowItemModal(true);
  }

  function closeItemModal() {
    setShowItemModal(false);
    setItemForm({});
    setItemImageFile(null);
  }

  async function handleItemImageUpload(file) {
    if (!file) return "";
    setItemUploading(true);
    try {
      const url = await uploadMenuImage(file);
      setItemUploading(false);
      return url;
    } catch (err) {
      setItemUploading(false);
      showToast("Image upload failed", "error");
      return "";
    }
  }

  async function handleSaveItem() {
    let imageUrl = itemForm.imageUrl;
    if (itemImageFile) {
      imageUrl = await handleItemImageUpload(itemImageFile);
    }
    const itemData = { ...itemForm, imageUrl };
    try {
      if (itemForm.id) {
        await api.put(`/menus/${activeMenuId}/items/${itemForm.id}`, itemData);
        showToast("Item updated!");
      } else {
        await api.post(`/menus/${activeMenuId}/items`, itemData);
        showToast("Item added!");
      }
      closeItemModal();
      fetchMenus();
    } catch (err) {
      showToast("Failed to save item", "error");
    }
  }

  async function handleDeleteItem(menuId, itemId) {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/menus/${menuId}/items/${itemId}`);
      fetchMenus();
      showToast("Item deleted!");
    } catch (err) {
      showToast("Failed to delete item", "error");
    }
  }

  async function handleToggleActive(menuId, itemId) {
    try {
      await api.patch(`/menus/${menuId}/items/${itemId}/active`);
      fetchMenus();
      showToast("Status updated!");
    } catch (err) {
      showToast("Failed to update status", "error");
    }
  }

  function showToast(message, type = "success") {
    setToast({ show: true, message, type });
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    toastTimeout.current = setTimeout(
      () => setToast({ show: false, message: "", type: "" }),
      3000
    );
  }

  function handleSelectMenu(menuId) {
    setActiveMenuId(menuId);
    setEditingMenuId(null);
    setEditingMenuName("");
    // Close sidebar on mobile after selection
    setSidebarOpen(false);
  }

  const selectedMenu = menus.find((m) => m.id === activeMenuId);
  const items = (selectedMenu?.items || []).filter(
    (item) =>
      (!showOutOfStock || !item.active) &&
      (item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.description.toLowerCase().includes(search.toLowerCase()))
  );

  function openAddMenuModal() {
    setMenuModalMode("add");
    setEditingMenuName("");
    setShowMenuModal(true);
  }

  function openEditMenuModal(id, currentName) {
    setMenuModalMode("edit");
    setEditingMenuId(id);
    setEditingMenuName(currentName);
    setShowMenuModal(true);
  }

  function closeMenuModal() {
    setShowMenuModal(false);
    setEditingMenuId(null);
    setEditingMenuName("");
  }

  const BASE_URL = process.env.REACT_APP_API_BASE_URL_IMG;
  function getFullImageUrl(imageUrl) {
    if (!imageUrl) return "";
    if (imageUrl.startsWith("http")) return imageUrl;
    const cleanUrl = imageUrl.replace(/^\/?.*uploads\//, "");
    return `${BASE_URL}/uploads/${cleanUrl}`;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center rounded-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="inline-block relative w-16 h-16">
            <motion.div
              className="absolute inset-0 border-4 border-rose-400 border-t-transparent rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="mt-4 text-rose-800 font-medium">Loading menus...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 rounded-2xl relative">
      {/* Mobile/Tablet Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className={`fixed md:relative z-50 md:z-auto w-64 md:w-64 bg-white border-r border-rose-100 p-4 md:p-6 flex flex-col gap-4 shadow-lg md:shadow-none h-full md:h-auto transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg lg:text-xl text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-amber-500">
            Categories
          </h2>
          <div className="flex items-center gap-2">
            <motion.button
              onClick={openAddMenuModal}
              className="btn btn-xs btn-primary rounded-full p-2"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Add Category"
            >
              <FaPlus />
            </motion.button>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-2 text-gray-500 hover:text-gray-700"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-1 flex-1 overflow-y-auto">
          <AnimatePresence>
            {menus.map((menu) => (
              <motion.div
                key={menu.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={`relative overflow-hidden rounded-lg ${
                  activeMenuId === menu.id
                    ? "bg-gradient-to-r from-rose-100 to-amber-100"
                    : "hover:bg-rose-50"
                }`}
              >
                <div
                  className="flex items-center justify-between px-3 lg:px-4 py-3 cursor-pointer"
                  onClick={() => handleSelectMenu(menu.id)}
                >
                  <span
                    className={`font-medium text-sm lg:text-base truncate ${
                      activeMenuId === menu.id
                        ? "text-rose-700"
                        : "text-gray-700"
                    }`}
                  >
                    {menu.name}
                  </span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditMenuModal(menu.id, menu.name);
                      }}
                      className="text-rose-400 hover:text-rose-600 transition-colors p-1"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaEdit size={12} />
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMenu(menu.id);
                      }}
                      className="text-rose-400 hover:text-rose-600 transition-colors p-1"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaTrash size={12} />
                    </motion.button>
                  </div>
                </div>
                {activeMenuId === menu.id && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-400 to-amber-400"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-6 w-full md:w-auto">
        {/* Mobile/Tablet Header */}
        <div className="flex items-center justify-between mb-4 md:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-gray-600 hover:text-gray-800"
          >
            <FaBars size={20} />
          </button>
          <h1 className="text-lg font-bold text-gray-800">Menu Management</h1>
          <div className="w-10"></div> {/* Spacer for centering */}
        </div>

        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4 mb-4 md:mb-6"
        >
          <div className="relative flex-1 w-full md:max-w-md">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              className="border border-gray-200 px-3 py-2 pl-10 rounded-full w-full focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent text-sm md:text-base"
              placeholder="Search items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2 md:gap-4 w-full md:w-auto">
            <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-full border border-gray-200 text-sm">
              <input
                type="checkbox"
                checked={showOutOfStock}
                onChange={(e) => setShowOutOfStock(e.target.checked)}
                className="form-checkbox h-4 w-4 text-rose-500 rounded focus:ring-rose-300"
              />
              <span className="text-xs md:text-sm">Show out of stock</span>
            </label>

            <motion.button
              onClick={() => selectedMenu && openItemModal(selectedMenu.id)}
              className="btn btn-success text-black px-3 md:px-4 py-2 rounded-full flex items-center gap-2 text-sm md:text-base"
              disabled={!selectedMenu}
              whileHover={selectedMenu ? { scale: 1.03 } : {}}
              whileTap={selectedMenu ? { scale: 0.97 } : {}}
            >
              <FaPlus />
              <span className="hidden sm:inline">Add Item</span>
              <span className="sm:hidden">Add</span>
            </motion.button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg overflow-hidden"
        >
          <div className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h3 className="text-lg md:text-xl font-bold text-gray-800">
                {selectedMenu?.name || "Select a category"}
              </h3>
              <div className="text-xs md:text-sm text-gray-500">
                {items.length} {items.length === 1 ? "item" : "items"}
              </div>
            </div>

            {items.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-8 md:py-12 text-gray-400"
              >
                {search || showOutOfStock
                  ? "No matching items found"
                  : "No items in this category"}
              </motion.div>
            ) : (
              <div className="divide-y divide-gray-100">
                <AnimatePresence>
                  {items.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      className="py-3 md:py-4 first:pt-0 last:pb-0"
                    >
                      <div className="flex items-start gap-3 md:gap-4">
                        {item.imageUrl && (
                          <motion.div
                            className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden shadow-sm flex-shrink-0"
                            whileHover={{ scale: 1.03 }}
                          >
                            <img
                              src={getFullImageUrl(item.imageUrl)}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </motion.div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-semibold text-gray-800 truncate text-sm md:text-base">
                              {item.name}
                            </h4>
                            <span className="font-bold text-rose-600 ml-2 text-sm md:text-base flex-shrink-0">
                              ₹{item.price}
                            </span>
                          </div>
                          {item.description && (
                            <p className="text-xs md:text-sm text-gray-500 mt-1 line-clamp-2">
                              {item.description}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-1 md:gap-2 ml-2 md:ml-4 flex-shrink-0">
                          <motion.button
                            onClick={() => openItemModal(selectedMenu.id, item)}
                            className="p-1 md:p-2 text-gray-500 hover:text-rose-500 rounded-full hover:bg-rose-50"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Edit"
                          >
                            <FaEdit size={12} className="md:w-3 md:h-3" />
                          </motion.button>

                          <motion.button
                            onClick={() =>
                              handleDeleteItem(selectedMenu.id, item.id)
                            }
                            className="p-1 md:p-2 text-gray-500 hover:text-rose-500 rounded-full hover:bg-rose-50"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Delete"
                          >
                            <FaTrash size={12} className="md:w-3 md:h-3" />
                          </motion.button>

                          <motion.div
                            className="flex items-center gap-1 ml-1 md:ml-2 cursor-pointer"
                            onClick={() =>
                              handleToggleActive(selectedMenu.id, item.id)
                            }
                            whileHover={{ scale: 1.05 }}
                          >
                            {item.active ? (
                              <FaToggleOn className="text-green-500 text-lg md:text-xl" />
                            ) : (
                              <FaToggleOff className="text-gray-400 text-lg md:text-xl" />
                            )}
                            <span className="text-xs hidden sm:inline">
                              {item.active ? "Active" : "Inactive"}
                            </span>
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </motion.div>
      </main>

      {/* Item Modal */}
      <AnimatePresence>
        {showItemModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            >
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
                onClick={closeItemModal}
              >
                <FaTimes size={20} />
              </button>

              <div className="p-4 md:p-6 text-black">
                <h3 className="text-lg md:text-xl font-bold mb-4 text-black bg-clip-text bg-gradient-to-r from-rose-500 to-amber-500">
                  {itemForm.id ? "Edit Item" : "Add Item"}
                </h3>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSaveItem();
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 md:px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent text-sm md:text-base"
                      value={itemForm.name || ""}
                      onChange={(e) =>
                        setItemForm((f) => ({ ...f, name: e.target.value }))
                      }
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Price (₹)
                    </label>
                    <input
                      type="number"
                      className="w-full px-3 md:px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent text-sm md:text-base"
                      value={itemForm.price || ""}
                      onChange={(e) =>
                        setItemForm((f) => ({ ...f, price: e.target.value }))
                      }
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Description
                    </label>
                    <textarea
                      className="w-full px-3 md:px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent text-sm md:text-base"
                      rows="3"
                      value={itemForm.description || ""}
                      onChange={(e) =>
                        setItemForm((f) => ({
                          ...f,
                          description: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Image
                    </label>
                    <motion.div
                      className={`border-2 border-dashed rounded-xl p-4 md:p-6 text-center cursor-pointer transition-all ${
                        dragActive
                          ? "border-rose-300 bg-rose-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                          setItemImageFile(e.dataTransfer.files[0]);
                        }
                      }}
                      onClick={() =>
                        document.getElementById("item-image-input").click()
                      }
                      whileHover={{ scale: 1.01 }}
                    >
                      {itemImageFile ? (
                        <div>
                          <img
                            src={URL.createObjectURL(itemImageFile)}
                            alt="Preview"
                            className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg mx-auto mb-2"
                          />
                          <div className="text-xs text-gray-500 truncate">
                            {itemImageFile.name}
                          </div>
                        </div>
                      ) : itemForm.imageUrl ? (
                        <img
                          src={getFullImageUrl(itemForm.imageUrl)}
                          alt="Preview"
                          className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg mx-auto mb-2"
                        />
                      ) : (
                        <div className="space-y-2">
                          <div className="text-gray-400 text-sm">
                            Drag & drop or click to select an image
                          </div>
                          <div className="text-xs text-gray-400">
                            Recommended size: 500x500px
                          </div>
                        </div>
                      )}
                      <input
                        id="item-image-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) =>
                          e.target.files[0] &&
                          setItemImageFile(e.target.files[0])
                        }
                      />
                      {itemUploading && (
                        <div className="mt-2 text-sm text-rose-500">
                          Uploading...
                        </div>
                      )}
                    </motion.div>
                  </div>

                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">
                        Status:
                      </span>
                      <motion.button
                        type="button"
                        onClick={() =>
                          setItemForm((f) => ({ ...f, active: !f.active }))
                        }
                        whileTap={{ scale: 0.9 }}
                      >
                        {itemForm.active ? (
                          <FaToggleOn className="text-green-500 text-xl md:text-2xl" />
                        ) : (
                          <FaToggleOff className="text-gray-400 text-xl md:text-2xl" />
                        )}
                      </motion.button>
                      <span
                        className={`text-sm font-medium ${
                          itemForm.active ? "text-green-600" : "text-gray-500"
                        }`}
                      >
                        {itemForm.active ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="flex gap-2 w-full md:w-auto">
                      <motion.button
                        type="button"
                        onClick={closeItemModal}
                        className="flex-1 md:flex-none px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg font-medium text-sm md:text-base"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        type="submit"
                        className="flex-1 md:flex-none px-4 py-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all text-sm md:text-base"
                        disabled={itemUploading}
                        whileHover={!itemUploading ? { scale: 1.05 } : {}}
                        whileTap={!itemUploading ? { scale: 0.95 } : {}}
                      >
                        {itemForm.id ? "Update Item" : "Save Item"}
                      </motion.button>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Menu Modal */}
      <AnimatePresence>
        {showMenuModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm relative overflow-hidden"
            >
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={closeMenuModal}
              >
                <FaTimes size={20} />
              </button>

              <div className="p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-amber-500">
                  {menuModalMode === "add" ? "Add Category" : "Edit Category"}
                </h3>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (menuModalMode === "add") {
                      await handleCreateMenu(e);
                    } else {
                      await handleUpdateMenu(editingMenuId);
                    }
                    closeMenuModal();
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Category Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 md:px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent text-sm md:text-base"
                      value={menuModalMode === "add" ? name : editingMenuName}
                      onChange={(e) =>
                        menuModalMode === "add"
                          ? setName(e.target.value)
                          : setEditingMenuName(e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <motion.button
                      type="button"
                      onClick={closeMenuModal}
                      className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 hover:text-gray-800 rounded-lg font-medium text-sm md:text-base"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all text-sm md:text-base"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {menuModalMode === "add" ? "Add" : "Update"}
                    </motion.button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-4 right-4 z-50 px-4 md:px-6 py-3 rounded-lg shadow-lg font-medium text-sm md:text-base max-w-xs md:max-w-md ${
              toast.type === "error"
                ? "bg-red-500 text-white"
                : "bg-green-500 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
